// Settings › SMS (wave 3, W2_Portal_Settings look): the SMS provider request
// and the custom mobile OTP switch (OTP sign-in depends on it).
//
// Save keeps the two separate writes of the old Submit, in the same order:
// editSmsConfig(details) for the provider, then editSettings({ ...settings,
// customMobileOTP }) for the switch. Only with critical edits on
// (settings.AllowCriticalEditsAdmin); in demo mode the provider values are
// hidden. The Authorization value is masked until someone presses the eye
// button and is never printed or logged.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { api } from 'common';
import { showToast, confirmDialog } from '../components/feedback';
import { SectionCard, SettingRow, SaveBar } from '../components/modern';

// What this tab saved, kept for the rest of the session while the store
// still holds what it started from (neither save updates the store).
let savedDetails = null; // { source, data }
let savedOtp = null; // { source, value }

const EMPTY_DETAILS = { apiUrl: '', method: '', authorization: '', contentType: '', body: '' };
const FIELDS = ['apiUrl', 'method', 'authorization', 'contentType', 'body'];
const text = (v) => (v === undefined || v === null ? '' : String(v));

// Wide screens (lg and up): the short settings in two columns, the request
// body across the row; one column below lg as before.
const ROWS_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' },
  columnGap: 4,
};

const detailsFrom = (smsDetails) => {
  if (savedDetails && savedDetails.source === smsDetails) return savedDetails.data;
  return smsDetails || EMPTY_DETAILS;
};
const otpFrom = (settings) => {
  if (savedOtp && settings && savedOtp.source === settings) return savedOtp.value;
  return !!(settings && settings.customMobileOTP);
};

const SmsSettings = () => {
  const { t, i18n } = useTranslation();
  const { editSmsConfig, editSettings } = api;
  const settings = useSelector((state) => state.settingsdata.settings);
  const smsDetails = useSelector((state) => state.smsconfigdata.smsDetails);
  const dispatch = useDispatch();

  const [baseData, setBaseData] = useState(() => detailsFrom(smsDetails));
  const [data, setData] = useState(() => detailsFrom(smsDetails));
  const [baseOtp, setBaseOtp] = useState(() => otpFrom(settings));
  const [customMobileOTP, setCustomMobileOTP] = useState(() => otpFrom(settings));
  const [showAuth, setShowAuth] = useState(false);
  const [saving, setSaving] = useState(false);
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const detailsRef = useRef(smsDetails);
  useEffect(() => {
    if (smsDetails !== detailsRef.current) {
      detailsRef.current = smsDetails;
      setBaseData(detailsFrom(smsDetails));
      setData(detailsFrom(smsDetails));
    }
  }, [smsDetails]);

  const settingsRef = useRef(settings);
  useEffect(() => {
    if (settings !== settingsRef.current) {
      settingsRef.current = settings;
      setBaseOtp(otpFrom(settings));
      setCustomMobileOTP(otpFrom(settings));
    }
  }, [settings]);

  const allowed = !!(settings && settings.AllowCriticalEditsAdmin);
  const hidden = t('hidden_demo');

  const changed = useMemo(() => {
    const out = FIELDS.filter((k) => text(data && data[k]) !== text(baseData && baseData[k]));
    if (customMobileOTP !== baseOtp) out.push('customMobileOTP');
    return out;
  }, [data, baseData, customMobileOTP, baseOtp]);

  const discard = async () => {
    if (!changed.length || saving) return;
    const ok = await confirmDialog({
      title:
        changed.length === 1
          ? t('settings_discard_title_one', { defaultValue: 'Discard 1 unsaved change?' })
          : t('settings_discard_title_many', { n: changed.length, defaultValue: 'Discard {{n}} unsaved changes?' }),
      body: t('settings_discard_body', { defaultValue: 'The settings go back to how they were when you last saved.' }),
      confirmLabel: t('settings_discard', { defaultValue: 'Discard' }),
      destructive: true,
    });
    if (ok && aliveRef.current) {
      setData(baseData);
      setCustomMobileOTP(baseOtp);
    }
  };

  const handleSubmit = async () => {
    if (saving) return;
    if (!allowed) {
      showToast.warning(t('demo_mode'));
      return;
    }
    const details = data;
    const otp = customMobileOTP;
    setSaving(true);
    try {
      dispatch(editSmsConfig(details));
      await dispatch(editSettings({ ...settings, customMobileOTP: otp }));
      savedDetails = { source: detailsRef.current, data: details };
      savedOtp = { source: settingsRef.current, value: otp };
      if (!aliveRef.current) return;
      setBaseData(details);
      setBaseOtp(otp);
      showToast.success(t('settings_updated', { defaultValue: 'Settings updated' }));
    } catch (e) {
      if (aliveRef.current) showToast.error(t('update_failed', { defaultValue: 'Update failed' }));
    } finally {
      if (aliveRef.current) setSaving(false);
    }
  };

  const authLabel = showAuth ? t('hide', { defaultValue: 'Hide' }) : t('show', { defaultValue: 'Show' });

  const field = (key, label, extra) => (
    <SettingRow
      key={key}
      id={`sms-${key}`}
      label={label}
      layout={key === 'body' ? 'stack' : undefined}
      sx={key === 'body' ? { gridColumn: '1 / -1' } : undefined}
      changed={text(data && data[key]) !== text(baseData && baseData[key])}
      control={
        <TextField
          id={`sms-${key}`}
          size="small"
          value={allowed ? text(data && data[key]) : hidden}
          disabled={!allowed}
          onChange={(e) => setData({ ...data, [key]: e.target.value })}
          inputProps={{ spellCheck: false, autoComplete: 'off' }}
          sx={{ width: key === 'body' ? '100%' : { xs: '100%', sm: 420 } }}
          {...(extra || {})}
        />
      }
    />
  );

  return (
    <Box dir={i18n.dir()}>
      <SectionCard id="sms" icon={<SmsOutlinedIcon />} hue="info" title={t('smssettings', { defaultValue: 'SMS settings' })} sx={{ display: 'flex', flexDirection: 'column', pb: 0 }}>
        <Box sx={{ mt: 1, pb: 1, ...ROWS_SX }}>
          {field('apiUrl', t('apiUrl'), { inputProps: { spellCheck: false, autoComplete: 'off', inputMode: 'url' } })}
          {field('method', t('method'))}
          {field('authorization', t('authorization'), {
            type: allowed && !showAuth ? 'password' : 'text',
            autoComplete: 'new-password',
            InputProps: allowed
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={authLabel}>
                        <IconButton edge="end" size="small" aria-label={`${authLabel}: ${t('authorization')}`} aria-pressed={showAuth ? 'true' : 'false'} onClick={() => setShowAuth((v) => !v)}>
                          {showAuth ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }
              : undefined,
          })}
          {field('contentType', t('contentType'))}
          {field('body', t('body'), { multiline: true, minRows: 3 })}
          <SettingRow
            id="sms-customMobileOTP"
            layout="switch"
            label={t('customMobileOTP')}
            changed={customMobileOTP !== baseOtp}
            control={
              <Switch
                id="sms-customMobileOTP"
                checked={customMobileOTP}
                onChange={(e) => setCustomMobileOTP(e.target.checked)}
                sx={{ my: -0.75 }}
              />
            }
          />
        </Box>
        <SaveBar
          changedCount={changed.length}
          onDiscard={discard}
          onSave={handleSubmit}
          saving={saving}
          sx={{ mx: { xs: -2, md: -2.5 }, px: { xs: 2, md: 2.5 } }}
        />
      </SectionCard>
    </Box>
  );
};

export default SmsSettings;
